import { Brain, Database, type LucideIcon, Palette, Rocket } from "lucide-react";
import { motion, useReducedMotion, type Variants } from "motion/react";
import { type ReactNode, useRef } from "react";
import { useTranslation } from "react-i18next";
import logoMark from "@/src/common/assets/images/logo.png";
import { REVEAL_EASE } from "@/src/common/components/animated-section";
import { CountUp } from "@/src/common/components/count-up";
import { EyebrowBadge } from "@/src/common/components/eyebrow-badge";
import { Reveal } from "@/src/common/components/reveal";
import { SectionHeading } from "@/src/common/components/section-heading";
import { toEntranceVariant, useEntrance } from "@/src/common/hooks/use-entrance";

interface ToolChip {
	label: string;
	x: number;
	y: number;
}

const TOOL_CHIPS: ReadonlyArray<ToolChip> = [
	{ label: "Slack", x: -230, y: -88 },
	{ label: "Google Calendar", x: 210, y: -96 },
	{ label: "Zoom", x: -150, y: 92 },
	{ label: "Mailchimp", x: 240, y: 70 },
	{ label: "Typeform", x: -260, y: 8 },
	{ label: "Eventbrite", x: 120, y: -30 },
	{ label: "Notion", x: 30, y: 110 },
	{ label: "WhatsApp", x: -60, y: -112 },
	{ label: "Calendly", x: 250, y: -18 },
	{ label: "Airtable", x: 90, y: 96 },
];

const CHIP_STAGGER = 0.07;
const CHIP_DURATION = 0.9;
const MARK_DELAY = CHIP_STAGGER * TOOL_CHIPS.length + 0.3;

function createChipVariants(chip: ToolChip, index: number, reduced: boolean): Variants {
	const spread = { x: chip.x, y: chip.y, scale: 1 };
	return {
		hidden: { ...spread, opacity: 1, transition: { duration: 0 } },
		visible: reduced
			? { ...spread, opacity: 0.7, transition: { duration: 0 } }
			: { x: 0, y: 0, opacity: 0.2, scale: 0.85, transition: { duration: CHIP_DURATION, ease: REVEAL_EASE, delay: index * CHIP_STAGGER } },
	};
}

function createMarkVariants(reduced: boolean): Variants {
	return {
		hidden: { opacity: reduced ? 1 : 0, scale: reduced ? 1 : 0.6, transition: { duration: 0 } },
		visible: {
			opacity: 1,
			scale: 1,
			transition: reduced ? { duration: 0 } : { type: "spring", stiffness: 260, damping: 18, delay: MARK_DELAY },
		},
	};
}

function ConvergenceIllustration() {
	const reduced = useReducedMotion() === true;
	const ref = useRef<HTMLDivElement>(null);
	const entrance = toEntranceVariant(useEntrance(ref, { amount: 0.6, disabled: reduced }));

	return (
		<div ref={ref} aria-hidden="true" className="relative mx-auto h-64 w-full max-w-xl overflow-hidden">
			<div
				className="pointer-events-none absolute inset-0"
				style={{ background: "radial-gradient(closest-side at 50% 50%, oklch(0.62 0.20 254 / 0.16), transparent)" }}
			/>
			{TOOL_CHIPS.map((chip, index) => (
				<motion.span
					key={chip.label}
					className="absolute left-1/2 top-1/2 inline-flex -translate-x-1/2 -translate-y-1/2 items-center gap-1.5 whitespace-nowrap rounded-full border border-default-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-default-600 shadow-sm"
					initial={false}
					animate={entrance}
					variants={createChipVariants(chip, index, reduced)}
				>
					<span className="size-1.5 rounded-full bg-default-400" />
					{chip.label}
				</motion.span>
			))}
			<motion.div
				className="absolute left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 flex-col items-center gap-2"
				initial={false}
				animate={entrance}
				variants={createMarkVariants(reduced)}
			>
				<span className="flex size-20 items-center justify-center rounded-3xl border border-default-200 bg-white shadow-lift">
					<img src={logoMark} alt="" decoding="async" className="size-12" />
				</span>
				<span className="rounded-full bg-brand-50 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.16em] text-brand-700">
					UpSquad
				</span>
			</motion.div>
		</div>
	);
}

interface MetricProps {
	to: number;
	suffix?: string;
	label: string;
	tone?: "ink" | "white";
}

function Metric({ to, suffix, label, tone = "ink" }: MetricProps) {
	const isInk = tone === "ink";
	return (
		<p className="flex items-baseline gap-2">
			<CountUp to={to} suffix={suffix} className={`text-4xl font-bold tracking-tight ${isInk ? "text-gradient-brand" : "text-white"}`} />
			<span className={`text-xs font-semibold uppercase tracking-[0.14em] ${isInk ? "text-default-500" : "text-white/70"}`}>{label}</span>
		</p>
	);
}

interface BentoCardProps {
	children: ReactNode;
	className?: string;
	tone?: "ink" | "brand";
	delay?: number;
}

function BentoCard({ children, className = "", tone = "ink", delay = 0 }: BentoCardProps) {
	const surface =
		tone === "brand"
			? "bg-brand-gradient border-transparent text-white hover:shadow-lift"
			: "border-default-200 bg-white hover:border-brand-200 hover:bg-brand-50/30 hover:shadow-lift";
	return (
		<Reveal delay={delay} className={className}>
			<article
				className={`flex h-full flex-col gap-4 rounded-3xl border p-6 shadow-card transition-[box-shadow,border-color,background-color] duration-300 md:p-7 ${surface}`}
			>
				{children}
			</article>
		</Reveal>
	);
}

function IconBadge({ icon: Icon, tone = "ink" }: { icon: LucideIcon; tone?: "ink" | "white" }) {
	const classes = tone === "ink" ? "bg-brand-50 text-brand-700" : "bg-white/15 text-white";
	return (
		<span className={`inline-flex size-11 items-center justify-center rounded-2xl ${classes}`}>
			<Icon className="h-5 w-5" />
		</span>
	);
}

export function UnifiedPlatformSection() {
	const { t } = useTranslation("home");

	return (
		<section className="bg-mesh relative isolate overflow-hidden py-24 md:py-32">
			<div aria-hidden="true" className="absolute inset-0 bg-grid-ink opacity-50" />

			<div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
				<SectionHeading
					eyebrow={<EyebrowBadge tone="ink">{t("unified.eyebrow")}</EyebrowBadge>}
					title={t("unified.title")}
					titleAccent={t("unified.title_accent")}
					description={t("unified.subtitle")}
				/>

				<div className="mt-16 grid gap-4 lg:grid-cols-12">
					<BentoCard className="lg:col-span-7">
						<ConvergenceIllustration />
						<div className="flex flex-col gap-3">
							<Metric to={10} suffix="+" label={t("unified.metrics.tools.label")} />
							<h3 className="text-lg font-bold text-foreground">{t("unified.stat_1.title")}</h3>
							<p className="text-sm text-default-600">{t("unified.stat_1.body")}</p>
						</div>
					</BentoCard>

					<BentoCard className="lg:col-span-5" tone="brand" delay={0.1}>
						<IconBadge icon={Brain} tone="white" />
						<Metric to={3} label={t("unified.metrics.ai.label")} tone="white" />
						<h3 className="text-lg font-bold">{t("unified.stat_2.title")}</h3>
						<p className="text-sm text-white/80">{t("unified.stat_2.body")}</p>
					</BentoCard>

					<BentoCard className="lg:col-span-4" delay={0.15}>
						<IconBadge icon={Database} />
						<Metric to={1} label={t("unified.metrics.truth.label")} />
						<h3 className="text-lg font-bold text-foreground">{t("unified.stat_3.title")}</h3>
						<p className="text-sm text-default-600">{t("unified.stat_3.body")}</p>
					</BentoCard>

					<BentoCard className="lg:col-span-4" delay={0.2}>
						<IconBadge icon={Rocket} />
						<Metric to={72} suffix="h" label={t("unified.metrics.launch.label")} />
						<h3 className="text-lg font-bold text-foreground">{t("unified.metrics.launch.title")}</h3>
						<p className="text-sm text-default-600">{t("unified.metrics.launch.body")}</p>
					</BentoCard>

					<BentoCard className="lg:col-span-4" delay={0.25}>
						<IconBadge icon={Palette} />
						<Metric to={100} suffix="%" label={t("unified.metrics.brand.label")} />
						<h3 className="text-lg font-bold text-foreground">{t("unified.metrics.brand.title")}</h3>
						<p className="text-sm text-default-600">{t("unified.metrics.brand.body")}</p>
					</BentoCard>
				</div>
			</div>
		</section>
	);
}
